import config from "_src/config";
import {vec2, vec3} from "_src/libs/gl-matrix";

export const screenUtils = {

  toScreen(globalViewport, localViewport, clientX, clientY) {

    const localX = clientX - globalViewport[0];
    const localY = clientY - globalViewport[1];

    const viewportX = localViewport[2];
    const viewportY = localViewport[3];
    const leftBottomX = localViewport[0];
    const leftBottomY = localViewport[1];

    const x = (localX - leftBottomX) / viewportX * 2 - 1;
    const y = (localY - leftBottomY) / viewportY * 2 - 1;

    return {x, y};
  },

  fromScreen(globalViewport, localViewport, screenX, screenY) {

    const viewportX = localViewport[2];
    const viewportY = localViewport[3];
    const leftBottomX = localViewport[0];
    const leftBottomY = localViewport[1];

    const x = (screenX + 1) / 2 * viewportX + leftBottomX + globalViewport[0];
    const y = (screenY + 1) / 2 * viewportY + leftBottomY + globalViewport[1];

    return {x, y};
  },

  getCanvasPhisycalSize(canvas) {

    const pixelRatio = this.getPixelRatio();

    const width = Math.max(pixelRatio * canvas.offsetWidth, 1);
    const height = Math.max(pixelRatio * canvas.offsetHeight, 1);

    return { width, height };

  },

  viewportToPhisycalSize(viewport, dest) {

    const pixelRatio = this.getPixelRatio();

    dest[0] = viewport[0] * pixelRatio;
    dest[1] = viewport[1] * pixelRatio;
    dest[2] = viewport[2] * pixelRatio;
    dest[3] = viewport[3] * pixelRatio;

    return dest;

  },

  virtualPointToPhysical(physicalPoint, virtualPoint) {
    const pixelRatio = this.getPixelRatio();

    physicalPoint[0] = virtualPoint[0] * pixelRatio;
    physicalPoint[1] = virtualPoint[1] * pixelRatio;

    return physicalPoint;
  },

  physicalPointToVirtual(virtualPoint, physicalPoint) {
    const pixelRatio = this.getPixelRatio();

    virtualPoint[0] = physicalPoint[0] / pixelRatio;
    virtualPoint[1] = physicalPoint[1] / pixelRatio;

    return virtualPoint;
  },

  clientToScreen(globalViewport, localViewport, clientX, clientY) {

    const pixelRatio = this.getPixelRatio();
    const physicalX = clientX * pixelRatio;
    const physicalY = clientY * pixelRatio;

    let {x, y} = this.toScreen(globalViewport, localViewport, physicalX, physicalY);
    y *= -1.0;

    return {x, y};
  },

  getPixelRatio() {
    return Math.min(window.devicePixelRatio || 1, config.maxPixelRatio);
  },

  calculateObjectScale: (function() {
    const _vec2Tmp = vec2.create();
    const _vec3Tmp1 = vec3.create();
    const _vec3Tmp2 = vec3.create();
    const _vec3Tmp3 = vec3.create();
    const _vec3Tmp4 = vec3.create();

    return function(virtualScreenSize, objectBoundingCircleDiameter, objectPosition, camera) {
      const pixelRatio = this.getPixelRatio();
      const physicalScreenSize = virtualScreenSize * pixelRatio;

      const zeroViewportPosition = vec2.set(_vec2Tmp,0,0);
      const localCameraXDirection = vec3.set(_vec3Tmp1, 1, 0, 0);
      const worldCameraXDirection = vec3.transformQuat(localCameraXDirection, localCameraXDirection, camera.quat);
      vec3.scale(worldCameraXDirection, worldCameraXDirection, objectBoundingCircleDiameter / 2);

      const spherePoint = vec3.add(_vec3Tmp2, objectPosition, worldCameraXDirection);
      const screenSpherePointRaw = camera.toScreenSpace(spherePoint, _vec3Tmp3);
      const screenSpherePoint = this.fromScreen(zeroViewportPosition, camera.viewport, screenSpherePointRaw[0], screenSpherePointRaw[1]);
      const screenSphereCenterRaw = camera.toScreenSpace(objectPosition, _vec3Tmp4);
      const screenSphereCenter = this.fromScreen(zeroViewportPosition, camera.viewport, screenSphereCenterRaw[0], screenSphereCenterRaw[1]);

      const screenSize = Math.abs(screenSphereCenter.x - screenSpherePoint.x) * 2;
      return physicalScreenSize / screenSize;
    }
  })(),

  calculateSegmentLinePeriod: (function() {
    const _vec2Tmp = vec2.create();
    const vec3Tmp1 = vec3.create();
    const vec3Tmp2 = vec3.create();

    return function(pos1, pos2, segmentScreenLength, camera) {
      const zeroViewportPosition = vec2.set(_vec2Tmp,0,0);

      const screenPosRaw1 = camera.toScreenSpace(pos1, vec3Tmp1);
      const screenPosRaw2 = camera.toScreenSpace(pos2, vec3Tmp2);
      const screenPos1 = screenUtils.fromScreen(zeroViewportPosition, camera.viewport, screenPosRaw1[0], screenPosRaw1[1]);
      const screenPos2 = screenUtils.fromScreen(zeroViewportPosition, camera.viewport, screenPosRaw2[0], screenPosRaw2[1]);

      const screenLength = Math.sqrt((screenPos2.x - screenPos1.x) * (screenPos2.x - screenPos1.x) + (screenPos2.y - screenPos1.y) * (screenPos2.y - screenPos1.y));
      const periodsCount = screenLength / segmentScreenLength;
      const worldLength = vec3.distance(pos1, pos2);

      return worldLength / periodsCount;
    }
  })(),

};

export const mouseUtils = {
  isLeftButtonPressed(e) {
    return Boolean(e.buttons & 0b001);
  },

  isRightButtonPressed(e) {
    return Boolean(e.buttons & 0b010);
  },

  isMiddleButtonPressed(e) {
    return Boolean(e.buttons & 0b100);
  },

  isAnyButtonPressed(e) {
    return this.isLeftButtonPressed(e) || this.isRightButtonPressed(e) || this.isMiddleButtonPressed(e);
  },
};

export function getPngImageFromPixels(pixels, width, height) {

  const canvas = document.createElement("canvas");
  const clampedPixels = new Uint8ClampedArray(pixels);
  const iData = new ImageData(clampedPixels, width, height);

  const ctx = canvas.getContext("2d");
  ctx.canvas.width = width;
  ctx.canvas.height = height;
  ctx.putImageData(iData, 0, 0);

  const dataURL = canvas.toDataURL("image/png").replace("image/png", "image/octet-stream");

  window.location.href=dataURL;

}
